
// Import Libraries
import { useEffect, useState } from 'react';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { usePrepareContractWrite, useContractWrite, useAccount, useContractRead, useWaitForTransaction, useBalance, useNetwork, useSwitchNetwork } from 'wagmi'
import { BigNumber } from 'ethers';
import { toast } from 'react-toastify';
import { Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useMediaQuery } from 'react-responsive'

// Import Components
import MintPage from './components/mintPage';
import NavBar from './components/navBar';
import Footer from './components/footer';
import Claim from './components/claim';
import Team from './components/team'
import './App.css'

// Import ABIs
import testBTRFLYV2abi from './ABI/testBTRFLYV2ABI.json'
import testBBCNFTabi from './ABI/testBBCABI.json'

// Set contracts
const BTRFLYV2addy = '0x4Bc4bbA990Fe31D529D987f7b8CcF79F1626e559' // TESTNET
const BBCNFTaddy = '0x01B3Be4b23a759D540a38D303d408545CC8eF6C5' // TESTNET

// Set functional values
const apprAmt = "157606113636680001099983"  // BTRFLYV2 Max supply * 10^18
const costBTRFLY = "1000000000000000000" // = 1 * 10^18
const maxMintQuantity = 20
const corrChainId = 5 // Chain ID for check - 1=Mainnet, 5=Goerli

function App() {

  const [mintQuantity, setMintQuantity] = useState(1)
  const [pendingToast, setPendingToast] = useState(null)

  const { openConnectModal } = useConnectModal();

  // Get network
  const { chain } = useNetwork()

  // Switch network prompt
  const { switchNetwork } = useSwitchNetwork({
    chainId: corrChainId
  })

  // Get isConnected state and account address
  const { address, isConnected } = useAccount({
    onConnect() {
      toast.success('Wallet Connected 🦋', { autoClose: 2000 })
    },
    onDisconnect() {
      toast.error('Wallet Disconnected', { autoClose: 2000 })
    }
  })

  // Prepare mint function
  const { config: mintConfig } = usePrepareContractWrite({
    address: BBCNFTaddy,
    abi: testBBCNFTabi,
    functionName: 'mintWithBtrfly',
    args: [
      mintQuantity,
      BigNumber.from((costBTRFLY * mintQuantity).toString())
    ]
  })

  // Write mint function
  const {
    data: mintData,
    isLoading: mintLoading,
    isSuccess: mintSent,
    writeAsync: handleMint
  } = useContractWrite({
    ...mintConfig,
    onSuccess() {
      setPendingToast(toast.loading('Mint Pending...'))
    }
  })

  // Prepare approve function
  const { config: approveConfig } = usePrepareContractWrite({
    address: BTRFLYV2addy,
    abi: testBTRFLYV2abi,
    functionName: 'approve',
    args: [
      BBCNFTaddy,
      BigNumber.from(apprAmt)
    ]
  })

  // Write approve function
  const {
    data: approveData,
    isLoading: approveLoading,
    isSuccess: approveSent,
    writeAsync: handleApprove,
  } = useContractWrite({
    ...approveConfig,
    onSuccess() {
      setPendingToast(toast.loading('Approval Pending...'))
    }
  })

  // Look for approve transaction success
  const { isSuccess: approveSuccess, isError: approveError } = useWaitForTransaction({
    hash: approveData?.hash,
    onSuccess() {
      refetchApprovedAmount?.()
      toast.update(pendingToast, { render: 'Approval Successful! 😎', type: 'success', isLoading: false, autoClose: '3000' })
    },
    onError() {
      toast.update(pendingToast, { render: 'Approval Error!', type: 'error', isLoading: false, autoClose: '3000' })
    }
  })

  // Look for mint transaction success
  const { isSuccess: mintSuccess } = useWaitForTransaction({
    hash: mintData?.hash,
    onSuccess() {
      refetchBTRFLYBalance?.()
      refetchBBCBalance?.()
      toast.update(pendingToast, { render: 'Mint Successful! 🥳', type: 'success', isLoading: false, autoClose: '3000' })
    },
    onError() {
      toast.update(pendingToast, { render: 'Mint Error!', type: 'error', isLoading: false, autoClose: '3000' })
    }
  })

  // Get BTRFLY approved amount
  const { data: approvedAmount, refetch: refetchApprovedAmount } = useContractRead({
    address: BTRFLYV2addy,
    abi: testBTRFLYV2abi,
    functionName: 'allowance',
    args: [
      address,
      BBCNFTaddy
    ]
  })

  // Get balance of user's BTRFLY
  const { data: BTRFLYbalance, refetch: refetchBTRFLYBalance } = useBalance({
    address: address,
    token: BTRFLYV2addy,
  })

  // Get balance of user's BBC NFTs
  const { data: bbcBalance, refetch: refetchBBCBalance } = useContractRead({
    address: BBCNFTaddy,
    abi: testBBCNFTabi,
    functionName: 'balanceOf',
    args: [
      address
    ]
  })

  const BBCBalance = bbcBalance?.toNumber()

  const statePack = {
    isConnected: isConnected,
    approvedAmount: approvedAmount,
    maxMintQuantity: maxMintQuantity,
    handleApprove: handleApprove,
    apprAmt: apprAmt,
    handleMint: handleMint,
    mintQuantity: mintQuantity,
    setMintQuantity: setMintQuantity,
    switchNetwork: switchNetwork,
    chain: chain,
    corrChainId: corrChainId,
    costBTRFLY: costBTRFLY,
    BTRFLYbalance: BTRFLYbalance,
    BBCBalance: BBCBalance
  }

  const location = useLocation()
  const isMobile = useMediaQuery({ query: '(max-width: 775px)' })

  return (
    <div className="siteContainer">

      <NavBar
        openConnectModal={openConnectModal}
        isConnected={isConnected}
        isMobile={isMobile}
      />

      <div className='contentContainer'>
        <AnimatePresence mode='wait'>
          <Routes key={location.pathname} location={location}>

            <Route path='/' element={<MintPage statePack={statePack} />} />

            <Route path='/claim' element={<Claim />} />

            <Route path='/team' element={<Team isMobile={isMobile} />} />

          </Routes>
        </AnimatePresence>

      </div>

      <Footer />

    </div>
  );
}

export default App;